import type { Video } from 'expo-av';
import { observer } from 'mobx-react-lite';
import { RefObject, useEffect } from 'react';

import { useVideoPlayerSettings } from './hooks/useVideoPlayerSettings';
import type { IVideoPlayerStore } from './VideoPlayer';
import { getHTMLVideoElementFromRef } from './videoPlayerUtils';

interface IVideoPlayerSettingsSetterProps {
  videoRef: RefObject<Video>;
  store: IVideoPlayerStore;
}

// We use this component for performance optimization by "catching" rerenders.
// We are subscribing this component to state changes that require us to react to them
// but causing not necessary renders in VideoPlayer component by this way we react on
// changes like `volume` that will cause a lot of rerenders and those rerenders cause
// performance issues in the more complicated component like VideoPlayer, so we are
// "catching" those rerenders in this component and react on them without causing
// performance issues
export const VideoPlayerSupportComponent =
  observer<IVideoPlayerSettingsSetterProps>(({ videoRef, store }) => {
    const videoPlayerSettings = useVideoPlayerSettings();

    // web
    useEffect(() => {
      // Where we are passing styles to Video component we are giving those styles
      // to View/div component that wraps the native Video component. In the web,
      // in order to force <video /> component to take a whole width and height
      // in the custom full-screen mode and not only part, we must set width and
      // height as 100% for <video /> component
      const videoElement = getHTMLVideoElementFromRef(videoRef);

      if (videoElement) {
        videoElement.style.height = '100%';
        videoElement.style.width = '100%';
        // We disable context menu for preventing the ability to download video
        videoElement.setAttribute('oncontextmenu', 'return false');
      }
    }, []);

    // web
    useEffect(() => {
      // Prop "rate" in Video component is not working for web, so we must
      // pass playbackRate directly to <video /> component
      const videoElement = getHTMLVideoElementFromRef(videoRef);

      if (videoElement) {
        videoElement.playbackRate = videoPlayerSettings.playbackRate;
      }
      // We also need to set rate after start playing
    }, [videoPlayerSettings.playbackRate, store.isPlaying]);

    // mobile
    useEffect(() => {
      videoRef.current?.setRateAsync(
        videoPlayerSettings.playbackRate,
        true,
      );
    }, [videoPlayerSettings.playbackRate]);

    // web
    useEffect(() => {
      videoRef.current?.setVolumeAsync(videoPlayerSettings.volume);
    }, [videoPlayerSettings.volume]);

    return null;
  });
